import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, Loader2, Search } from "lucide-react";

const STATUS_LABELS = { new: "Yeni", contacted: "İletişime Geçildi", converted: "Dönüştü", lost: "Kaybedildi" };
const STATUS_COLORS = { new: "bg-blue-500/15 text-blue-400", contacted: "bg-yellow-500/15 text-yellow-400", converted: "bg-emerald-500/15 text-emerald-400", lost: "bg-red-500/15 text-red-400" };

export default function AdminLeads() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  const { data: leads, isLoading } = useQuery({
    queryKey: ["admin-leads", status, search],
    queryFn: async () => {
      const params = {};
      if (status !== "all") params.status = status;
      if (search) params.search = search;
      return (await api.get("/admin/leads", { params })).data;
    },
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "leads.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="admin-leads-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-medium text-white">Leadler</h1>
          <p className="mt-1 text-sm text-slate-400">İletişim formundan gelen tüm talepler.</p>
        </div>
        <Button
          onClick={handleExport}
          disabled={exporting}
          variant="outline"
          className="border-white/10 text-white hover:bg-white/5"
          data-testid="export-leads-csv-button"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          CSV İndir
        </Button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Ara: ad, telefon, e-posta"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-white/5 border-white/10 pl-9 text-white"
            data-testid="leads-search-input"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white" data-testid="leads-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Durumlar</SelectItem>
            <SelectItem value="new">Yeni</SelectItem>
            <SelectItem value="contacted">İletişime Geçildi</SelectItem>
            <SelectItem value="converted">Dönüştü</SelectItem>
            <SelectItem value="lost">Kaybedildi</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 rounded-xl border border-white/10 bg-[#0A0A0A]">
        {isLoading ? (
          <div className="flex justify-center p-10">
            <Loader2 className="h-6 w-6 animate-spin text-gold" />
          </div>
        ) : (leads || []).length === 0 ? (
          <p className="p-10 text-center text-sm text-slate-400" data-testid="leads-empty-state">Sonuç bulunamadı.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="text-slate-400">Ad Soyad</TableHead>
                <TableHead className="text-slate-400">Telefon</TableHead>
                <TableHead className="text-slate-400">İşletme Türü</TableHead>
                <TableHead className="text-slate-400">Durum</TableHead>
                <TableHead className="text-slate-400">Tarih</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.map((lead) => (
                <TableRow
                  key={lead._id}
                  className="cursor-pointer border-white/5 hover:bg-white/5"
                  onClick={() => navigate(`/admin/leads/${lead._id}`)}
                  data-testid={`lead-row-${lead._id}`}
                >
                  <TableCell>
                    <Link to={`/admin/leads/${lead._id}`} className="text-sm font-medium text-white hover:text-gold-light" data-testid={`lead-link-${lead._id}`}>
                      {lead.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm text-slate-400">{lead.phone}</TableCell>
                  <TableCell className="text-sm text-slate-400">{lead.business_type || "-"}</TableCell>
                  <TableCell>
                    <Badge className={`${STATUS_COLORS[lead.status]} border-none`}>{STATUS_LABELS[lead.status]}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-400">
                    {new Date(lead.created_at).toLocaleDateString("tr-TR")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
